import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell } from "../../components/care/CareLayout";
import {
  emptyInventoryFilters,
  InventoryFilterPanel,
} from "../../components/care/InventoryFilterPanel";
import VehicleImage from "../../components/care/VehicleImage";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useSiteProfile from "../../hooks/useSiteProfile";
import { careService } from "../../services/careService";

const emptyHomeData = {
  vehicles: [],
  inquiries: [],
};

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const vehicleTitle = (vehicle = {}) =>
  firstText(
    vehicle.title,
    vehicle.displayName,
    vehicle.name,
    [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" "),
    "Vehicle",
  );

const vehicleId = (vehicle = {}) => vehicle.id || vehicle.vehicleId || vehicle.vin || "";

const cleanSelectOptionValue = (value) =>
  String(value || "").replace(/\s*[[(][^\])]*[\])]\s*$/g, "").trim();

const numberFrom = (value) => {
  const numeric = Number(String(value || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const statusToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const displayStatus = (value) => {
  const raw = firstText(value, "Ready");
  return statusToken(raw) === "ready" ? "Available" : raw.replace(/_/g, " ");
};

const formatMileage = (value) => {
  const numeric = numberFrom(value);
  return numeric ? `${numeric.toLocaleString("en-US")} miles` : firstText(value, "Mileage pending");
};

const imageUrlFor = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return /^(https?:|\/|data:image|blob:)/i.test(trimmed) ? trimmed : "";
  }
  if (!value || typeof value !== "object") return "";
  return imageUrlFor(firstText(value.signedUrl, value.url, value.photoUrl, value.imageUrl, value.downloadUrl, value.fileUrl, value.publicUrl));
};

const normalizeHomeVehicle = (vehicle = {}, index = 0) => {
  const priceSource = firstText(vehicle.price, vehicle.askingPrice, vehicle.listPrice);
  const mileageSource = firstText(vehicle.mileage, vehicle.odometer);
  const photo = imageUrlFor(vehicle.mainPhoto) || (Array.isArray(vehicle.photos) ? vehicle.photos.map(imageUrlFor).find(Boolean) : "");
  const status = displayStatus(firstText(vehicle.status, vehicle.inventoryStatus, statusOf(vehicle), "Ready"));
  return {
    ...vehicle,
    id: firstText(vehicleId(vehicle), vehicle.vin, vehicleTitle(vehicle), index),
    title: vehicleTitle(vehicle),
    make: cleanSelectOptionValue(vehicle.make),
    model: cleanSelectOptionValue(vehicle.model),
    trim: cleanSelectOptionValue(vehicle.trim),
    bodyType: cleanSelectOptionValue(firstText(vehicle.bodyType, vehicle.bodyStyle, vehicle.type)),
    year: firstText(vehicle.year),
    yearValue: numberFrom(vehicle.year) || 0,
    fuel: cleanSelectOptionValue(firstText(vehicle.fuelType, vehicle.fuel)),
    drivetrain: cleanSelectOptionValue(firstText(vehicle.drivetrain, vehicle.driveType)),
    engine: cleanSelectOptionValue(vehicle.engine),
    transmission: cleanSelectOptionValue(vehicle.transmission),
    exteriorColor: cleanSelectOptionValue(firstText(vehicle.exteriorColor, vehicle.color)) || "Color pending",
    interiorColor: cleanSelectOptionValue(vehicle.interiorColor),
    price: formatMoney(priceSource),
    priceValue: numberFrom(priceSource) || 0,
    mileage: formatMileage(mileageSource),
    mileageValue: numberFrom(mileageSource) || 0,
    status,
    statusKey: statusToken(status),
    condition: cleanSelectOptionValue(firstText(vehicle.condition, vehicle.vehicleCondition, vehicle.saleCondition)),
    photo,
    meta: [vehicle.engine, vehicle.transmission, vehicle.drivetrain || vehicle.driveType].map(cleanSelectOptionValue).filter(Boolean).join(" | ") || "Details pending",
  };
};

const uniqueTextOptions = (items) =>
  [...new Set(items.map((item) => String(item || "").trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const inquiryName = (inquiry = {}) =>
  firstText(
    [inquiry.firstName, inquiry.lastName].filter(Boolean).join(" "),
    inquiry.customerName,
    inquiry.name,
    inquiry.email,
    "Customer inquiry",
  );

const statusOf = (record = {}) =>
  String(record.workflowStatus || record.processStatus || record.status || record.state || "")
    .trim()
    .toUpperCase();

const isOpenStatus = (status) =>
  !["ARCHIVED", "CLOSED", "COMPLETED", "COMPLETE", "DECLINED", "CANCELED", "CANCELLED", "DELETED"].includes(status);

const dateValue = (record = {}) =>
  record.updatedAt || record.createdAt || record.createdDate || record.receivedAt || "";

const formatDateTime = (value) => {
  if (!value) return "Recently";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatMoney = (value) => {
  const numeric = Number(String(value || "").replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(numeric) || !numeric) return "Price pending";
  return numeric.toLocaleString("en-US", {
    currency: "USD",
    maximumFractionDigits: 0,
    style: "currency",
  });
};

export default function HomePage() {
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [homeData, setHomeData] = useState(emptyHomeData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inventoryFilters, setInventoryFilters] = useState({ ...emptyInventoryFilters });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { siteProfile } = useSiteProfile();

  const actorId = user?.id || user?.userId || "";
  const workspaceName = firstText(siteProfile.name, selectedWorkspace?.name, selectedWorkspace?.workspaceName, selectedWorkspace?.businessName, "Auto Portal");

  useEffect(() => {
    let active = true;

    const loadHome = async () => {
      if (!workspaceListReady) return;
      if (!selectedWorkspaceId) {
        setHomeData(emptyHomeData);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const workspaceParams = {
        wid: selectedWorkspaceId,
        workspaceId: selectedWorkspaceId,
        actorId,
        sort: "createdAt,desc",
        size: 8,
      };

      const [vehiclesResult, inquiriesResult] = await Promise.allSettled([
        careService.vehicles.list(workspaceParams),
        careService.customerInquiries.list(workspaceParams),
      ]);

      if (!active) return;

      setHomeData({
        vehicles: vehiclesResult.status === "fulfilled" ? pageItems(vehiclesResult.value) : [],
        inquiries: inquiriesResult.status === "fulfilled" ? pageItems(inquiriesResult.value) : [],
      });
      setError(
        vehiclesResult.status === "rejected" || inquiriesResult.status === "rejected"
          ? "Some dealership data could not be loaded."
          : "",
      );
      setLoading(false);
    };

    loadHome().catch((nextError) => {
      if (!active) return;
      setHomeData(emptyHomeData);
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load dealership home.");
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [actorId, selectedWorkspaceId, workspaceListReady]);

  const summary = useMemo(() => {
    const readyVehicles = homeData.vehicles.filter((vehicle) => ["ACTIVE", "AVAILABLE", "READY"].includes(statusOf(vehicle)));
    const pendingVehicles = homeData.vehicles.filter((vehicle) => ["PENDING", "NEW", "IN_REVIEW", "INCOMING"].includes(statusOf(vehicle)));
    const soldVehicles = homeData.vehicles.filter((vehicle) => ["SOLD", "COMPLETED"].includes(statusOf(vehicle)));
    const openInquiries = homeData.inquiries.filter((inquiry) => isOpenStatus(statusOf(inquiry)));

    return {
      openInquiries,
      pendingVehicles,
      readyVehicles,
      soldVehicles,
    };
  }, [homeData.inquiries, homeData.vehicles]);

  const recentActivity = useMemo(() => {
    const inquiryRows = homeData.inquiries.map((inquiry) => ({
      badge: statusOf(inquiry) || "Inquiry",
      detail: firstText(inquiry.email, inquiry.phone, inquiry.zipCode, "Customer availability request"),
      icon: "ti-message-circle",
      time: dateValue(inquiry),
      title: inquiryName(inquiry),
      to: "/customer-inquiries",
    }));

    const vehicleRows = homeData.vehicles.map((vehicle) => ({
      badge: statusOf(vehicle) || "Inventory",
      detail: firstText(formatMoney(vehicle.price || vehicle.askingPrice || vehicle.listPrice), vehicle.mileage, "Vehicle record updated"),
      icon: "ti-car",
      time: dateValue(vehicle),
      title: vehicleTitle(vehicle),
      to: vehicleId(vehicle) ? `/vehicles/${vehicleId(vehicle)}` : "/vehicles",
    }));

    return [...inquiryRows, ...vehicleRows]
      .sort((a, b) => new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime())
      .slice(0, 5);
  }, [homeData.inquiries, homeData.vehicles]);

  const homeInventory = useMemo(
    () => homeData.vehicles.map((vehicle, index) => normalizeHomeVehicle(vehicle, index)),
    [homeData.vehicles],
  );
  const inventoryFilterOptions = useMemo(() => ({
    makes: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.make)),
    models: uniqueTextOptions(
      (inventoryFilters.make
        ? homeInventory.filter((vehicle) => vehicle.make === cleanSelectOptionValue(inventoryFilters.make))
        : homeInventory).map((vehicle) => vehicle.model),
    ),
    trims: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.trim)),
    bodyTypes: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.bodyType)),
    conditions: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.condition)),
    years: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.year)).sort((a, b) => Number(b) - Number(a)),
    fuels: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.fuel)),
    drivetrains: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.drivetrain)),
    engines: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.engine)),
    exteriorColors: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.exteriorColor)),
    interiorColors: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.interiorColor)),
    transmissions: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.transmission)),
    statuses: uniqueTextOptions(homeInventory.map((vehicle) => vehicle.status)),
  }), [homeInventory, inventoryFilters.make]);
  const filteredHomeInventory = useMemo(() => {
    const needle = inventoryFilters.query.trim().toLowerCase();
    const activeStatusToken = inventoryFilters.status ? statusToken(displayStatus(inventoryFilters.status)) : "";
    return homeInventory.filter((vehicle) => {
      const searchText = [
        vehicle.title,
        vehicle.make,
        vehicle.model,
        vehicle.trim,
        vehicle.bodyType,
        vehicle.year,
        vehicle.fuel,
        vehicle.drivetrain,
        vehicle.engine,
        vehicle.transmission,
        vehicle.exteriorColor,
        vehicle.interiorColor,
        vehicle.status,
        vehicle.condition,
        vehicle.meta,
      ].filter(Boolean).join(" ").toLowerCase();
      if (needle && !searchText.includes(needle)) return false;
      if (inventoryFilters.make && vehicle.make !== cleanSelectOptionValue(inventoryFilters.make)) return false;
      if (inventoryFilters.model && vehicle.model !== cleanSelectOptionValue(inventoryFilters.model)) return false;
      if (inventoryFilters.trim && vehicle.trim !== cleanSelectOptionValue(inventoryFilters.trim)) return false;
      if (inventoryFilters.bodyType && vehicle.bodyType !== cleanSelectOptionValue(inventoryFilters.bodyType)) return false;
      if (inventoryFilters.condition && vehicle.condition !== cleanSelectOptionValue(inventoryFilters.condition)) return false;
      if (inventoryFilters.engine && vehicle.engine !== cleanSelectOptionValue(inventoryFilters.engine)) return false;
      if (inventoryFilters.fuel && vehicle.fuel !== cleanSelectOptionValue(inventoryFilters.fuel)) return false;
      if (inventoryFilters.drivetrain && vehicle.drivetrain !== cleanSelectOptionValue(inventoryFilters.drivetrain)) return false;
      if (inventoryFilters.transmission && vehicle.transmission !== cleanSelectOptionValue(inventoryFilters.transmission)) return false;
      if (inventoryFilters.exteriorColor && vehicle.exteriorColor !== cleanSelectOptionValue(inventoryFilters.exteriorColor)) return false;
      if (inventoryFilters.interiorColor && vehicle.interiorColor !== cleanSelectOptionValue(inventoryFilters.interiorColor)) return false;
      if (activeStatusToken && !["newest_cars"].includes(activeStatusToken) && vehicle.statusKey !== activeStatusToken) return false;
      if (inventoryFilters.minYear && vehicle.yearValue < Number(inventoryFilters.minYear)) return false;
      if (inventoryFilters.maxYear && vehicle.yearValue > Number(inventoryFilters.maxYear)) return false;
      if (inventoryFilters.minPrice && vehicle.priceValue < Number(inventoryFilters.minPrice)) return false;
      if (inventoryFilters.maxPrice && vehicle.priceValue > Number(inventoryFilters.maxPrice)) return false;
      if (inventoryFilters.minMileage && vehicle.mileageValue < Number(inventoryFilters.minMileage)) return false;
      if (inventoryFilters.maxMileage && vehicle.mileageValue > Number(inventoryFilters.maxMileage)) return false;
      return true;
    });
  }, [homeInventory, inventoryFilters]);
  const maxInventoryPrice = Math.max(0, ...homeInventory.map((vehicle) => vehicle.priceValue));
  const maxInventoryMileage = Math.max(0, ...homeInventory.map((vehicle) => vehicle.mileageValue));
  const activeMinPrice = Number(inventoryFilters.minPrice || 0);
  const activeMaxPrice = Number(inventoryFilters.maxPrice || maxInventoryPrice || 0);
  const activeMinMileage = Number(inventoryFilters.minMileage || 0);
  const activeMaxMileage = Number(inventoryFilters.maxMileage || maxInventoryMileage || 0);
  const hasActiveInventoryFilters = Object.values(inventoryFilters).some(Boolean);
  const updateInventoryFilter = (field, value) => {
    setInventoryFilters((current) => ({
      ...current,
      [field]: value,
      ...(field === "make" ? { model: "" } : {}),
    }));
  };
  const resetInventoryFilters = () => {
    setInventoryFilters({ ...emptyInventoryFilters });
  };

  const metrics = [
    ["ti-car", "Inventory", homeData.vehicles.length, `${summary.readyVehicles.length} ready`],
    ["ti-message-circle", "Open inquiries", summary.openInquiries.length, "Need follow-up"],
    ["ti-clock-hour-4", "Pending units", summary.pendingVehicles.length, "Prep or review"],
    ["ti-rosette-discount-check", "Sold", summary.soldVehicles.length, "Closed units"],
  ];
  const quickSections = [
    ["ti-calendar-event", "Appointments", "Today", "Test drives and handoffs", "/schedules?status=today"],
    ["ti-message-circle", "Inquiries", "Open", `${summary.openInquiries.length} open leads`, "/customer-inquiries"],
    ["ti-car-plus", "Add Vehicle", "Create", "Create inventory record", "/vehicles/new"],
    ["ti-rosette-discount-check", "Recent Sales", "Sold", `${summary.soldVehicles.length} sold units`, "/vehicles?query=sold"],
    ["ti-sparkles", "New Inventory", "New", `${summary.pendingVehicles.length} pending units`, "/vehicles?query=new"],
  ];

  return (
    <CareShell className="dashboard-content workspace care-home-page" siteProfile={siteProfile}>
      <section className="home-action-panel home-dealer-shell">
        <div className="home-action-head home-dealer-hero">
          <div className="page-title">
            <span className="section-eyebrow">{workspaceName}</span>
            <h1>Dealer Home</h1>
            <p>Inventory health, customer inquiries, and the next actions that keep the lot moving.</p>
          </div>
          <div className="home-status-card home-dealer-status">
            <span className="round-icon blue"><i className="ti ti-building-store" /></span>
            <div>
              <strong>{loading ? "Syncing store data" : "Store snapshot"}</strong>
              <small>{loading ? "Loading inventory and leads" : `${homeData.vehicles.length} units · ${summary.openInquiries.length} open inquiries`}</small>
            </div>
          </div>
        </div>

        {error ? <p className="form-error">{error}</p> : null}

        <div className="home-dealer-actions" aria-label="Primary dealer actions">
          <Link className="btn primary" to="/vehicles"><i className="ti ti-list-details" /> View Inventory</Link>
          <Link className="btn secondary" to="/customer-inquiries"><i className="ti ti-message-circle" /> Customer Inquiries</Link>
          <Link className="btn primary add-vehicle-button" to="/vehicles/new"><i className="ti ti-car-plus" /> Add Vehicle</Link>
        </div>
      </section>

      <section className="home-quick-block" aria-label="Quick sections">
        <div className="home-section-head">
          <h2>Quick sections</h2>
          <span>Fast dealer actions</span>
        </div>
        <div className="home-quick-sections">
          {quickSections.map(([icon, title, status, note, to]) => (
            <Link className="home-quick-card" to={to} key={title}>
              <span><i className={`ti ${icon}`} /></span>
              <div>
                <strong>{title}</strong>
                <small>{note}</small>
              </div>
              <b>{status}</b>
              <i className="ti ti-arrow-up-right" />
            </Link>
          ))}
        </div>
      </section>

      <section className="metric-grid home-overview-metrics" aria-label="Dealership overview">
        {metrics.map(([icon, label, value, note]) => (
          <article className="metric-card home-glass-card" key={label}>
            <span className="round-icon blue"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{loading ? "..." : value}</b><small>{note}</small></div>
          </article>
        ))}
      </section>

      <section className="home-dealer-panel home-inventory-panel" aria-label="Filtered inventory">
        <div className="home-section-head">
          <h2>Inventory</h2>
          <span>{loading ? "Loading" : `${filteredHomeInventory.length} matches`}</span>
        </div>
        <label className="auto-hero-search vehicles-inline-search">
          <i className="ti ti-search" />
          <input
            aria-label="Search inventory"
            placeholder="Search make, model, trim, color, body style, or feature"
            value={inventoryFilters.query}
            onChange={(event) => updateInventoryFilter("query", event.target.value)}
          />
        </label>
        <button
          className="auto-mobile-filter-toggle"
          type="button"
          onClick={() => setMobileFiltersOpen((isOpen) => !isOpen)}
          aria-controls="home-inventory-filters"
          aria-expanded={mobileFiltersOpen}
        >
          <i className="ti ti-adjustments-horizontal" />
          {mobileFiltersOpen ? "Hide filters" : "Filter"}
        </button>
        <div className={`auto-inventory-layout logged-in-inventory-layout${mobileFiltersOpen ? " filters-open" : ""}`} id="home-inventory-filters">
          <InventoryFilterPanel
            activeMinMileage={activeMinMileage}
            activeMaxMileage={activeMaxMileage}
            activeMinPrice={activeMinPrice}
            activeMaxPrice={activeMaxPrice}
            filterOptions={inventoryFilterOptions}
            filters={inventoryFilters}
            hasActiveFilters={hasActiveInventoryFilters}
            maxMileage={maxInventoryMileage}
            maxPrice={maxInventoryPrice}
            matchCount={filteredHomeInventory.length}
            onReset={resetInventoryFilters}
            onUpdateFilter={updateInventoryFilter}
            priceLabel={formatMoney}
          />
          <div className="auto-inventory-grid">
            {loading ? (
              <p className="auto-inventory-empty">Loading vehicles...</p>
            ) : filteredHomeInventory.length ? filteredHomeInventory.map((vehicle) => (
              <Link className="auto-inventory-card home-inventory-card" to={`/vehicles/${vehicle.id}`} key={vehicle.id}>
                <div className="auto-car-visual">
                  <VehicleImage src={vehicle.photo} alt={vehicle.title} />
                </div>
                <div className="auto-inventory-card-body">
                  <span className={`auto-inventory-status is-${vehicle.statusKey || "ready"}`}>{vehicle.status}</span>
                  <h3>{vehicle.title}</h3>
                  <strong>{vehicle.price}</strong>
                  <p>{vehicle.mileage} - {vehicle.exteriorColor}</p>
                  <small>{vehicle.meta}</small>
                </div>
              </Link>
            )) : (
              <p className="auto-inventory-empty">No vehicles match these filters.</p>
            )}
          </div>
        </div>
      </section>

      <section className="home-dealer-panel">
        <div className="home-section-head">
          <h2>Recent activity</h2>
          <span>{loading ? "Loading" : `${recentActivity.length} latest updates`}</span>
        </div>
        {loading ? <p>Loading dealership activity...</p> : recentActivity.length ? recentActivity.map((item) => (
          <Link className="list-row home-activity-row" to={item.to} key={`${item.title}-${item.time}-${item.detail}`}>
            <span className="tiny-icon"><i className={`ti ${item.icon}`} /></span>
            <div><strong>{item.title}</strong><small>{item.detail} · {formatDateTime(item.time)}</small></div>
            <em>{item.badge.replace(/_/g, " ")}</em>
          </Link>
        )) : <p>No recent dealership activity found.</p>}
      </section>
    </CareShell>
  );
}
